export const sanitizeName = (str) => {
    if (!str) return '';
    return String(str)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9 \-\[\]]/g, '')
        .replace(/\s+/g, ' ')
        .replace(/-+/g, '-')
        .trim();
};

export const getIsoDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getIsoDateTimeLabel = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `[${year}-${month}-${day} ${hours}h${minutes}]`;
};

const normalizeVersion = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return 1;
    return Math.floor(parsed);
};

const withImmutablePrefix = (baseName, version = 1) => {
    const safeBaseName = sanitizeName(baseName) || 'Documento - Cliente';
    const safeVersion = normalizeVersion(version);
    return sanitizeName(`${getIsoDateTimeLabel()} - v${safeVersion} - ${safeBaseName}`) || `${getIsoDateTimeLabel()} - v${safeVersion} - Documento - Cliente`;
};

const uniq = (items) => Array.from(new Set((Array.isArray(items) ? items : []).map((item) => sanitizeName(item)).filter(Boolean)));

const joinFriendlyNames = (items) => {
    const names = uniq(items);
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} e ${names[1]}`;
    return `${names[0]} e Outros`;
};

const getClientQualification = (partyLike) => partyLike?.qualificationData?.qualification || partyLike?.qualification || {};

const getRepresentativeName = (partyLike, contextData = {}) => {
    const explicitRepresentative = sanitizeName(contextData?.representativeName || contextData?.responsibleName || '');
    if (explicitRepresentative) return explicitRepresentative;

    const qualification = getClientQualification(partyLike);
    const representativeCandidates = [
        qualification?.animal?.responsible,
        qualification?.animal?.guardian,
        qualification?.minor?.representative,
        qualification?.legal?.representative,
        qualification?.estate?.inventorier,
        qualification?.estate?.representative,
        qualification?.estate?.inventor
    ];

    for (const candidate of representativeCandidates) {
        const name = sanitizeName(candidate?.socialName || candidate?.name || '');
        if (name) return name;
    }

    return '';
};

const getRepresentativeNames = (partyLike, contextData = {}) => {
    const explicitNames = uniq(contextData?.representativeNames);
    if (explicitNames.length > 0) return explicitNames;

    const explicitSingle = sanitizeName(contextData?.representativeName || contextData?.responsibleName || '');
    if (explicitSingle) return [explicitSingle];

    const derived = getRepresentativeName(partyLike, contextData);
    return derived ? [derived] : [];
};

export const generateDocumentFileName = (docType, mainParty, contextData = {}, version = 1) => {
    const normalizedDocType = sanitizeName(docType).toLowerCase();
    const includeImmutablePrefix = Boolean(contextData?.includeImmutablePrefix || contextData?.immutableVersioning || contextData?.includeVersionPrefix);
    const mainNames = uniq(contextData?.mainPartyNames);
    const mainName = mainNames.length > 0
        ? joinFriendlyNames(mainNames)
        : sanitizeName(
            contextData?.mainPartyName ||
            mainParty?.name ||
            mainParty?.qualificationData?.name ||
            mainParty?.qualification?.animal?.name ||
            mainParty?.qualification?.legal?.companyName ||
            mainParty?.qualification?.minor?.name ||
            mainParty?.qualification?.physical?.socialName ||
            mainParty?.qualification?.physical?.name ||
            'Cliente'
        ) || 'Cliente';

    if (normalizedDocType === 'procuracao') {
        let base = `Procuracao - ${mainName}`;
        const representativeName = joinFriendlyNames(getRepresentativeNames(mainParty, contextData));
        if (representativeName) base += ` - Resp ${representativeName}`;
        return includeImmutablePrefix ? withImmutablePrefix(base, version) : (sanitizeName(base) || 'Procuracao - Cliente');
    }

    if (normalizedDocType === 'contrato') {
        let base = `Contrato - ${mainName}`;
        const ramoDireito = sanitizeName(contextData?.ramoDireito || '');
        if (ramoDireito) base += ` - ${ramoDireito}`;
        return includeImmutablePrefix ? withImmutablePrefix(base, version) : (sanitizeName(base) || 'Contrato - Cliente');
    }

    if (normalizedDocType === 'auto') {
        const processRef = sanitizeName(contextData?.processNumber || contextData?.processoNumero || contextData?.shortName || contextData?.processName || 'Processo');
        const base = `Auto - ${processRef} - ${mainName}`;
        return includeImmutablePrefix ? withImmutablePrefix(base, version) : (sanitizeName(base) || 'Auto - Processo - Cliente');
    }

    if (normalizedDocType === 'whatsapp') {
        const base = `Pericia WhatsApp - ${mainName}`;
        return includeImmutablePrefix ? withImmutablePrefix(base, version) : (sanitizeName(base) || 'Pericia WhatsApp - Cliente');
    }

    if (normalizedDocType === 'triagem') {
        const originalName = sanitizeName(contextData?.originalFileName || contextData?.fileName || contextData?.originalName || 'Documento');
        const base = `Triagem - ${originalName}`;
        return includeImmutablePrefix ? withImmutablePrefix(base, version) : (sanitizeName(base) || 'Triagem - Documento');
    }

    const fallbackBase = `${docType || 'Documento'} - ${mainName}`;
    return includeImmutablePrefix ? withImmutablePrefix(fallbackBase, version) : (sanitizeName(fallbackBase) || 'Documento - Cliente');
};

export const generateDocumentFileStem = (docType, mainParty, contextData = {}, version = 1) => {
    const friendlyName = generateDocumentFileName(docType, mainParty, contextData, version);
    return sanitizeName(friendlyName)
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '') || 'Documento-Cliente';
};

export const generatePublishedArtifactFileName = (docType, mainParty, contextData = {}, version = 1) => {
    const normalizedDocType = sanitizeName(docType).toLowerCase();
    const mainNames = uniq(contextData?.mainPartyNames);
    const mainName = mainNames.length > 0
        ? joinFriendlyNames(mainNames)
        : sanitizeName(
            contextData?.mainPartyName ||
            mainParty?.name ||
            mainParty?.qualificationData?.name ||
            mainParty?.qualification?.animal?.name ||
            mainParty?.qualification?.legal?.companyName ||
            mainParty?.qualification?.minor?.name ||
            mainParty?.qualification?.physical?.socialName ||
            mainParty?.qualification?.physical?.name ||
            'Cliente'
        ) || 'Cliente';

    if (normalizedDocType === 'procuracao') {
        return withImmutablePrefix(`Procuracao - ${mainName}`, version);
    }

    if (normalizedDocType === 'contrato') {
        const ramoDireito = sanitizeName(contextData?.ramoDireito || '');
        const base = ramoDireito ? `Contrato - ${mainName} - ${ramoDireito}` : `Contrato - ${mainName}`;
        return withImmutablePrefix(base, version);
    }

    return withImmutablePrefix(`${sanitizeName(docType || 'Documento')} - ${mainName}`, version);
};

export const generatePublishedArtifactFileStem = (docType, mainParty, contextData = {}, version = 1) => {
    return sanitizeName(generatePublishedArtifactFileName(docType, mainParty, contextData, version))
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '') || 'Documento-Cliente';
};
